import React, { useState, useEffect, useCallback } from 'react';
import useFeedback from '../hooks/useFeedback';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  Alert,
  Chip,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Grid,
  MenuItem,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  EventNote as HolidayIcon,
  Star as DefaultIcon,
  StarBorder as SetDefaultIcon,
  InfoOutlined as InfoIcon,
} from '@mui/icons-material';
import {
  getHolidaySchedules,
  getFederalHolidayOptions,
  getHolidayScheduleHolidays,
  createHolidaySchedule,
  updateHolidaySchedule,
  deleteHolidaySchedule,
  setDefaultHolidaySchedule,
  createHoliday,
  updateHoliday,
  deleteHoliday,
  generateHolidayEntries,
} from '../api';
import { useAuth } from '../context/AuthContext';
import { useConfirmDialog } from './ConfirmDialog';

const endOfYear = () => `${new Date().getFullYear()}-12-31`;
const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const emptyScheduleForm = { name: '', description: '', shift_observed: true, is_default: false };

export default function HolidayManagement() {
  const [schedules, setSchedules] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [customHolidays, setCustomHolidays] = useState([]);
  const [federalOptions, setFederalOptions] = useState([]);
  const [previewYear, setPreviewYear] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, showSuccess, clearSuccess] = useFeedback(5000);

  // Schedule create/edit dialog
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  const [scheduleEditMode, setScheduleEditMode] = useState(false);
  const [scheduleForm, setScheduleForm] = useState(emptyScheduleForm);

  // Custom holiday dialog
  const [holidayDialogOpen, setHolidayDialogOpen] = useState(false);
  const [holidayEditMode, setHolidayEditMode] = useState(false);
  const [currentHoliday, setCurrentHoliday] = useState(null);
  const [holidayForm, setHolidayForm] = useState({ name: '', holiday_date: '', is_active: true });

  const { showConfirm, ConfirmDialogComponent } = useConfirmDialog();
  const { hasPermission } = useAuth();
  const canGenerate = hasPermission('can_run_payroll');

  // Paid holiday entry generation (admin/payroll admin only)
  const [generateDialogOpen, setGenerateDialogOpen] = useState(false);
  const [generateForm, setGenerateForm] = useState({ start_date: todayStr(), end_date: endOfYear() });
  const [generating, setGenerating] = useState(false);
  const [payHoursDraft, setPayHoursDraft] = useState(null);
  const [savingHours, setSavingHours] = useState(false);
  const [workedMultDraft, setWorkedMultDraft] = useState(null);

  const selectedSchedule = schedules.find(s => s.id === selectedId) || null;

  const loadSchedules = useCallback(async (preferredId) => {
    try {
      setLoading(true);
      const res = await getHolidaySchedules();
      const list = res.data || [];
      setSchedules(list);
      if (list.length > 0) {
        const preferred = list.find(s => s.id === preferredId);
        const def = list.find(s => s.is_default);
        setSelectedId((preferred || def || list[0]).id);
      } else {
        setSelectedId(null);
      }
    } catch (err) {
      setError('Failed to load holiday schedules');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadDetail = useCallback(async (scheduleId, year) => {
    if (!scheduleId) {
      setCustomHolidays([]);
      return;
    }
    try {
      setDetailLoading(true);
      const [holidaysRes, federalRes] = await Promise.all([
        getHolidayScheduleHolidays(scheduleId),
        getFederalHolidayOptions(year),
      ]);
      setCustomHolidays(holidaysRes.data || []);
      setFederalOptions(federalRes.data || []);
    } catch (err) {
      setError('Failed to load schedule details');
      console.error(err);
    } finally {
      setDetailLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSchedules();
  }, [loadSchedules]);

  useEffect(() => {
    loadDetail(selectedId, previewYear);
  }, [selectedId, previewYear, loadDetail]);

  // --- Schedule actions ---

  const handleNewSchedule = () => {
    setScheduleForm(emptyScheduleForm);
    setScheduleEditMode(false);
    setScheduleDialogOpen(true);
  };

  const handleEditSchedule = () => {
    if (!selectedSchedule) return;
    setScheduleForm({
      name: selectedSchedule.name,
      description: selectedSchedule.description || '',
      shift_observed: selectedSchedule.shift_observed,
      is_default: selectedSchedule.is_default,
    });
    setScheduleEditMode(true);
    setScheduleDialogOpen(true);
  };

  const handleSaveSchedule = async () => {
    if (!scheduleForm.name.trim()) {
      setError('Schedule name is required');
      return;
    }
    try {
      if (scheduleEditMode && selectedSchedule) {
        await updateHolidaySchedule(selectedSchedule.id, {
          name: scheduleForm.name,
          description: scheduleForm.description,
          shift_observed: scheduleForm.shift_observed,
        });
        showSuccess('Schedule updated');
        setScheduleDialogOpen(false);
        loadSchedules(selectedSchedule.id);
      } else {
        // federal_holidays omitted: new schedules start with all federal holidays selected
        const res = await createHolidaySchedule({
          name: scheduleForm.name,
          description: scheduleForm.description,
          shift_observed: scheduleForm.shift_observed,
          is_default: scheduleForm.is_default,
        });
        showSuccess('Schedule created');
        setScheduleDialogOpen(false);
        loadSchedules(res.data.id);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save schedule');
    }
  };

  const handleDeleteSchedule = async () => {
    if (!selectedSchedule) return;
    const confirmed = await showConfirm({
      title: 'Delete Holiday Schedule',
      message: `Delete "${selectedSchedule.name}"? Its custom holidays are removed and any assigned employees fall back to the tenant default schedule.`,
      confirmText: 'Delete',
      confirmColor: 'error',
      severity: 'warning',
    });
    if (!confirmed) return;
    try {
      await deleteHolidaySchedule(selectedSchedule.id);
      showSuccess('Schedule deleted');
      loadSchedules();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete schedule');
    }
  };

  const handleSetDefault = async () => {
    if (!selectedSchedule || selectedSchedule.is_default) return;
    try {
      await setDefaultHolidaySchedule(selectedSchedule.id);
      showSuccess(`"${selectedSchedule.name}" is now the default schedule`);
      loadSchedules(selectedSchedule.id);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to set default schedule');
    }
  };

  const patchSelectedSchedule = (patch) => {
    setSchedules(prev => prev.map(s => (s.id === selectedId ? { ...s, ...patch } : s)));
  };

  const handleToggleFederal = async (key) => {
    if (!selectedSchedule) return;
    const current = selectedSchedule.federal_holidays || {};
    const updated = { ...current, [key]: current[key] !== true };
    patchSelectedSchedule({ federal_holidays: updated });
    try {
      await updateHolidaySchedule(selectedSchedule.id, { federal_holidays: updated });
    } catch (err) {
      patchSelectedSchedule({ federal_holidays: current });
      setError(err.response?.data?.error || 'Failed to update federal holidays');
    }
  };

  const handleToggleShiftObserved = async () => {
    if (!selectedSchedule) return;
    const newValue = !selectedSchedule.shift_observed;
    patchSelectedSchedule({ shift_observed: newValue });
    try {
      await updateHolidaySchedule(selectedSchedule.id, { shift_observed: newValue });
    } catch (err) {
      patchSelectedSchedule({ shift_observed: !newValue });
      setError(err.response?.data?.error || 'Failed to update schedule');
    }
  };

  // --- Holiday pay ---

  const handleTogglePaidHolidays = async () => {
    if (!selectedSchedule) return;
    const newValue = !selectedSchedule.paid_holidays_enabled;
    patchSelectedSchedule({ paid_holidays_enabled: newValue });
    try {
      await updateHolidaySchedule(selectedSchedule.id, { paid_holidays_enabled: newValue });
    } catch (err) {
      patchSelectedSchedule({ paid_holidays_enabled: !newValue });
      setError(err.response?.data?.error || 'Failed to update schedule');
    }
  };

  const handleSavePayHours = async () => {
    if (!selectedSchedule || payHoursDraft === null) return;
    // Same normalization as the server: half-hour steps in [0.5, 12]
    const hours = Math.round(parseFloat(payHoursDraft) * 2) / 2;
    setPayHoursDraft(null);
    if (Number.isNaN(hours) || hours < 0.5 || hours > 12) {
      setError('Holiday pay hours must be between 0.5 and 12');
      return;
    }
    const previous = selectedSchedule.holiday_pay_hours;
    patchSelectedSchedule({ holiday_pay_hours: hours });
    try {
      setSavingHours(true);
      const res = await updateHolidaySchedule(selectedSchedule.id, { holiday_pay_hours: hours });
      // Trust the server's stored value so the UI never drifts from the DB
      patchSelectedSchedule({ holiday_pay_hours: parseFloat(res.data.holiday_pay_hours) });
    } catch (err) {
      patchSelectedSchedule({ holiday_pay_hours: previous });
      setError(err.response?.data?.error || 'Failed to update holiday pay hours');
    } finally {
      setSavingHours(false);
    }
  };

  const handleSaveWorkedMultiplier = async () => {
    if (!selectedSchedule || workedMultDraft === null) return;
    const mult = Math.round(parseFloat(workedMultDraft) * 100) / 100;
    setWorkedMultDraft(null);
    if (Number.isNaN(mult) || mult < 1 || mult > 3) {
      setError('Worked holiday rate must be between 1.0 and 3.0');
      return;
    }
    const previous = selectedSchedule.worked_holiday_multiplier;
    patchSelectedSchedule({ worked_holiday_multiplier: mult });
    try {
      const res = await updateHolidaySchedule(selectedSchedule.id, { worked_holiday_multiplier: mult });
      patchSelectedSchedule({ worked_holiday_multiplier: parseFloat(res.data.worked_holiday_multiplier) });
    } catch (err) {
      patchSelectedSchedule({ worked_holiday_multiplier: previous });
      setError(err.response?.data?.error || 'Failed to update worked holiday rate');
    }
  };

  const handleGenerateEntries = async () => {
    try {
      setGenerating(true);
      const res = await generateHolidayEntries(generateForm);
      setGenerateDialogOpen(false);
      const d = res.data;
      const notes = [];
      if (d.skipped > 0) notes.push(`${d.skipped} already existed`);
      if (d.skipped_locked > 0) notes.push(`${d.skipped_locked} in locked pay periods`);
      if (d.skipped_no_rate > 0) notes.push(`${d.skipped_no_rate} employees skipped — no pay rate set`);
      if (d.skipped_outside_employment > 0) notes.push(`${d.skipped_outside_employment} outside hire/termination dates`);
      showSuccess(`Created ${d.created} holiday ${d.created === 1 ? 'entry' : 'entries'}` +
        (notes.length ? ` (${notes.join('; ')})` : ''));
    } catch (err) {
      setError((err.response?.data?.error || 'Failed to generate holiday entries') +
        ' — re-running is safe; existing entries are always skipped.');
    } finally {
      setGenerating(false);
    }
  };

  // --- Custom holiday actions ---

  const handleAddHoliday = () => {
    setHolidayForm({ name: '', holiday_date: '', is_active: true });
    setHolidayEditMode(false);
    setCurrentHoliday(null);
    setHolidayDialogOpen(true);
  };

  const handleEditHoliday = (holiday) => {
    setHolidayForm({
      name: holiday.name,
      holiday_date: holiday.holiday_date.split('T')[0],
      is_active: holiday.is_active,
    });
    setHolidayEditMode(true);
    setCurrentHoliday(holiday);
    setHolidayDialogOpen(true);
  };

  const handleSaveHoliday = async () => {
    if (!holidayForm.name || !holidayForm.holiday_date) {
      setError('Name and date are required');
      return;
    }
    try {
      if (holidayEditMode && currentHoliday) {
        await updateHoliday(currentHoliday.id, holidayForm);
        showSuccess('Holiday updated');
      } else {
        await createHoliday({ ...holidayForm, schedule_id: selectedId });
        showSuccess('Holiday created');
      }
      setHolidayDialogOpen(false);
      loadDetail(selectedId, previewYear);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save holiday');
    }
  };

  const handleDeleteHoliday = async (holiday) => {
    const confirmed = await showConfirm({
      title: 'Delete Holiday',
      message: `Delete "${holiday.name}" on ${formatDisplayDate(holiday.holiday_date, { year: 'numeric', month: 'short', day: 'numeric' })}?`,
      confirmText: 'Delete',
      confirmColor: 'error',
      severity: 'warning',
    });
    if (!confirmed) return;
    try {
      await deleteHoliday(holiday.id);
      showSuccess('Holiday deleted');
      loadDetail(selectedId, previewYear);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete holiday');
    }
  };

  const handleToggleHolidayActive = async (holiday) => {
    try {
      await updateHoliday(holiday.id, {
        ...holiday,
        holiday_date: holiday.holiday_date.split('T')[0],
        is_active: !holiday.is_active,
      });
      loadDetail(selectedId, previewYear);
    } catch (err) {
      setError('Failed to toggle holiday');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const currentYear = new Date().getFullYear();
  const yearOptions = [];
  for (let y = currentYear - 1; y <= currentYear + 5; y++) yearOptions.push(y);

  const formatDisplayDate = (dateStr, opts = { weekday: 'short', month: 'short', day: 'numeric' }) =>
    new Date(dateStr.split('T')[0] + 'T00:00:00').toLocaleDateString('en-US', opts);

  const federalSelection = selectedSchedule?.federal_holidays || {};
  const selectedFederalCount = Object.values(federalSelection).filter(Boolean).length;

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
        <HolidayIcon sx={{ mr: 1 }} />
        Holiday Schedules
        <Tooltip title="A holiday schedule is a named set of holidays. Assign schedules to employees on the People screen; anyone without an assignment follows the tenant default schedule. Federal holidays roll forward to future years automatically." arrow>
          <InfoIcon fontSize="small" sx={{ ml: 1, color: 'text.secondary' }} />
        </Tooltip>
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={clearSuccess}>{success}</Alert>}

      {/* Schedule selector toolbar */}
      <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          select
          size="small"
          label="Schedule"
          value={selectedId || ''}
          onChange={(e) => { setPayHoursDraft(null); setSelectedId(parseInt(e.target.value, 10)); }}
          InputLabelProps={{ shrink: true }}
          sx={{ minWidth: 240 }}
        >
          {schedules.map(s => (
            <MenuItem key={s.id} value={s.id}>
              {s.name}{s.is_default ? ' (default)' : ''}{!s.is_active ? ' (inactive)' : ''}
            </MenuItem>
          ))}
        </TextField>
        <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={handleNewSchedule}>
          New Schedule
        </Button>
        {selectedSchedule && (
          <>
            <Tooltip title="Rename this schedule or change its description and weekend-observance setting" arrow>
              <IconButton size="small" onClick={handleEditSchedule}><EditIcon fontSize="small" /></IconButton>
            </Tooltip>
            {!selectedSchedule.is_default && (
              <>
                <Tooltip title="Make this the tenant default — employees without an assigned schedule follow it" arrow>
                  <IconButton size="small" onClick={handleSetDefault}><SetDefaultIcon fontSize="small" /></IconButton>
                </Tooltip>
                <Tooltip title="Delete this schedule" arrow>
                  <IconButton size="small" color="error" onClick={handleDeleteSchedule}><DeleteIcon fontSize="small" /></IconButton>
                </Tooltip>
              </>
            )}
            {selectedSchedule.is_default && (
              <Chip icon={<DefaultIcon />} label="Default" size="small" color="primary" variant="outlined" />
            )}
            <Chip
              label={`${selectedSchedule.assigned_employee_count} assigned`}
              size="small"
              variant="outlined"
            />
          </>
        )}
      </Box>

      {!selectedSchedule && (
        <Alert severity="info">No holiday schedules yet. Create one to get started.</Alert>
      )}

      {selectedSchedule && (
        <Grid container spacing={2}>
          {/* Federal holidays */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1, flexWrap: 'wrap' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, flex: 1 }}>
                  Federal Holidays ({selectedFederalCount} of {federalOptions.length})
                </Typography>
                <TextField
                  select
                  size="small"
                  label="Preview Year"
                  value={previewYear}
                  onChange={(e) => setPreviewYear(parseInt(e.target.value, 10))}
                  InputLabelProps={{ shrink: true }}
                  sx={{ width: 130 }}
                >
                  {yearOptions.map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
                </TextField>
              </Box>
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={selectedSchedule.shift_observed}
                    onChange={handleToggleShiftObserved}
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography variant="body2">Observe weekend holidays on weekdays</Typography>
                    <Tooltip title="When a holiday falls on a Saturday it is observed the preceding Friday; Sunday holidays are observed the following Monday. This matches the federal observance convention." arrow>
                      <InfoIcon sx={{ ml: 0.5, fontSize: 16, color: 'text.secondary' }} />
                    </Tooltip>
                  </Box>
                }
                sx={{ mb: 1 }}
              />
              {detailLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}><CircularProgress size={24} /></Box>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableBody>
                      {federalOptions.map(f => {
                        const dateStr = selectedSchedule.shift_observed ? f.observed_date : f.actual_date;
                        const shifted = selectedSchedule.shift_observed && f.observed_date !== f.actual_date;
                        return (
                          <TableRow key={f.key} hover sx={{ cursor: 'pointer' }} onClick={() => handleToggleFederal(f.key)}>
                            <TableCell padding="checkbox">
                              <Checkbox size="small" checked={federalSelection[f.key] === true} />
                            </TableCell>
                            <TableCell>{f.name}</TableCell>
                            <TableCell align="right">
                              <Typography variant="body2" color="text.secondary">
                                {formatDisplayDate(dateStr)}
                                {shifted && (
                                  <Tooltip title={`Actual date: ${formatDisplayDate(f.actual_date)} — shifted to the observed weekday`} arrow>
                                    <Typography component="span" variant="caption" sx={{ ml: 0.5, color: 'warning.main' }}>
                                      (observed)
                                    </Typography>
                                  </Tooltip>
                                )}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Paper>
          </Grid>

          {/* Custom holidays */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, flex: 1, display: 'flex', alignItems: 'center' }}>
                  Custom Holidays
                  <Tooltip title="Company-specific holidays with a fixed date (e.g. the day after Thanksgiving, a company anniversary). Unlike federal holidays, these do not repeat — add them per year." arrow>
                    <InfoIcon sx={{ ml: 0.5, fontSize: 16, color: 'text.secondary' }} />
                  </Tooltip>
                </Typography>
                <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={handleAddHoliday}>
                  Add
                </Button>
              </Box>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold' }}>Date</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Name</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Active</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }} align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {customHolidays.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} align="center">
                          <Typography color="text.secondary" variant="body2" sx={{ py: 2 }}>
                            No custom holidays on this schedule.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      customHolidays.map((h) => (
                        <TableRow key={h.id} sx={{ opacity: h.is_active ? 1 : 0.5 }}>
                          <TableCell>
                            {formatDisplayDate(h.holiday_date, { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' })}
                          </TableCell>
                          <TableCell>{h.name}</TableCell>
                          <TableCell>
                            <Switch size="small" checked={h.is_active} onChange={() => handleToggleHolidayActive(h)} />
                          </TableCell>
                          <TableCell align="right">
                            <IconButton size="small" onClick={() => handleEditHoliday(h)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton size="small" color="error" onClick={() => handleDeleteHoliday(h)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>

          {/* Holiday pay */}
          <Grid item xs={12}>
            <Paper sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center' }}>
                  Holiday Pay
                  <Tooltip title="When enabled, generated holiday entries pay hourly employees on this schedule for each holiday: a non-billable time entry at their pay rate for the configured hours. Salaried employees are excluded (their pay is fixed). Use the Generate button to create entries for a date range — safe to re-run, existing entries are skipped." arrow>
                    <InfoIcon sx={{ ml: 0.5, fontSize: 16, color: 'text.secondary' }} />
                  </Tooltip>
                </Typography>
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={selectedSchedule.paid_holidays_enabled === true}
                      onChange={handleTogglePaidHolidays}
                    />
                  }
                  label={<Typography variant="body2">Paid holidays for hourly employees</Typography>}
                />
                <TextField
                  size="small"
                  type="number"
                  label="Hours per holiday"
                  value={payHoursDraft !== null ? payHoursDraft : (selectedSchedule.holiday_pay_hours ?? 8)}
                  onChange={(e) => setPayHoursDraft(e.target.value)}
                  onBlur={handleSavePayHours}
                  disabled={selectedSchedule.paid_holidays_enabled !== true}
                  inputProps={{ step: 0.5, min: 0.5, max: 12 }}
                  InputLabelProps={{ shrink: true }}
                  sx={{ width: 140 }}
                />
                {/* Info icon beside the field, not a Tooltip around it — a wrapped
                    field opens its tooltip on focus and covers neighbours on phones. */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <TextField
                    size="small"
                    type="number"
                    label="Worked holiday rate"
                    value={workedMultDraft !== null ? workedMultDraft : (selectedSchedule.worked_holiday_multiplier ?? 1)}
                    onChange={(e) => setWorkedMultDraft(e.target.value)}
                    onBlur={handleSaveWorkedMultiplier}
                    inputProps={{ step: 0.05, min: 1, max: 3 }}
                    InputProps={{ startAdornment: '×' }}
                    InputLabelProps={{ shrink: true }}
                    sx={{ width: 150 }}
                  />
                  <Tooltip title="Pay-rate multiplier for hours WORKED on a holiday from this schedule — 1.0 = normal rate, 1.5 = time-and-a-half. Applied to new entries when they are created (manual or clock-out). Client billing premiums are set per client (Clients screen) or per assignment (Bill Rates matrix)." arrow>
                    <InfoIcon fontSize="small" color="action" />
                  </Tooltip>
                </Box>
                {canGenerate && (
                  <Tooltip title="Creates holiday entries for ALL hourly employees in the tenant whose schedule has paid holidays enabled (not just this schedule)" arrow>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => {
                        setGenerateForm({ start_date: todayStr(), end_date: endOfYear() });
                        setGenerateDialogOpen(true);
                      }}
                    >
                      Generate Entries…
                    </Button>
                  </Tooltip>
                )}
              </Box>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Schedule create/edit dialog */}
      <Dialog open={scheduleDialogOpen} onClose={() => setScheduleDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{scheduleEditMode ? 'Edit Schedule' : 'New Holiday Schedule'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Schedule Name"
                value={scheduleForm.name}
                onChange={(e) => setScheduleForm(prev => ({ ...prev, name: e.target.value }))}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={scheduleForm.description}
                onChange={(e) => setScheduleForm(prev => ({ ...prev, description: e.target.value }))}
                multiline
                rows={2}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={scheduleForm.shift_observed}
                    onChange={(e) => setScheduleForm(prev => ({ ...prev, shift_observed: e.target.checked }))}
                  />
                }
                label="Observe weekend holidays on weekdays (Sat → Fri, Sun → Mon)"
              />
            </Grid>
            {!scheduleEditMode && (
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={scheduleForm.is_default}
                      onChange={(e) => setScheduleForm(prev => ({ ...prev, is_default: e.target.checked }))}
                    />
                  }
                  label="Make this the tenant default schedule"
                />
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setScheduleDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveSchedule}>
            {scheduleEditMode ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Custom holiday add/edit dialog */}
      <Dialog open={holidayDialogOpen} onClose={() => setHolidayDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{holidayEditMode ? 'Edit Holiday' : 'Add Custom Holiday'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Holiday Name"
                value={holidayForm.name}
                onChange={(e) => setHolidayForm(prev => ({ ...prev, name: e.target.value }))}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Date"
                type="date"
                value={holidayForm.holiday_date}
                onChange={(e) => setHolidayForm(prev => ({ ...prev, holiday_date: e.target.value }))}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHolidayDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveHoliday}>
            {holidayEditMode ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Generate holiday entries dialog */}
      <Dialog open={generateDialogOpen} onClose={() => setGenerateDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Generate Holiday Entries</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Creates a paid, non-billable time entry for each holiday in the range, for every active hourly
            employee whose holiday schedule has paid holidays enabled. Safe to re-run — existing holiday
            entries are skipped.
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="From"
                value={generateForm.start_date}
                onChange={(e) => setGenerateForm(prev => ({ ...prev, start_date: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="To"
                value={generateForm.end_date}
                onChange={(e) => setGenerateForm(prev => ({ ...prev, end_date: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGenerateDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleGenerateEntries}
            disabled={generating || savingHours || !generateForm.start_date || !generateForm.end_date}
            startIcon={generating ? <CircularProgress size={18} /> : null}
          >
            {generating ? 'Generating…' : 'Generate'}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialogComponent />
    </Box>
  );
}
